/**
 * RiverNarrativeBanner — Bande de narration doctrinale de la chaîne de valeur.
 *
 * V23.15.60 — À côté de la rivière, une bande discrète qui rend LE verdict
 * systémique du dirigeant (oligopole, monopole amont/aval, désintermédiation
 * possible, position d'arbitrage, dépendance critique, équilibre diffus).
 *
 * Auto-refresh :
 *   - Chargée au montage (utilise le cache 24h si la rivière n'a pas bougé)
 *   - Re-fetch si la signature des nœuds change (débouncé 800ms pour ne pas
 *     spammer pendant un What-if)
 *   - Bouton "Actualiser" force la régénération (bypass cache)
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Waves, RefreshCw, Loader2, Sparkles, Shield, AlertTriangle, Users2, TrendingUp, Zap, Scale } from "lucide-react";

const VERDICT_META = {
  monopole_amont: {
    label: "Monopole amont",
    icon: Shield,
    color: "#7F77DD",
    hint: "Un acteur amont concentre la valeur et le pouvoir.",
  },
  monopole_aval: {
    label: "Monopole aval",
    icon: Shield,
    color: "#1D9E75",
    hint: "Un acteur aval concentre la valeur et le pouvoir.",
  },
  oligopole: {
    label: "Oligopole",
    icon: Users2,
    color: "#EF9F27",
    hint: "2-3 acteurs se partagent >60% de la valeur.",
  },
  desintermediation_possible: {
    label: "Désintermédiation possible",
    icon: Zap,
    color: "#378ADD",
    hint: "Vous pouvez court-circuiter certains intermédiaires.",
  },
  dependance_critique: {
    label: "Dépendance critique",
    icon: AlertTriangle,
    color: "#E24B4A",
    hint: "Vous dépendez d'un point de contrôle unique.",
  },
  position_arbitrage: {
    label: "Position d'arbitrage",
    icon: TrendingUp,
    color: "#EF9F27",
    hint: "Vous êtes central et pouvez arbitrer amont/aval.",
  },
  equilibre_diffus: {
    label: "Équilibre diffus",
    icon: Scale,
    color: "#71717A",
    hint: "Pas de concentration marquée dans la rivière.",
  },
};

function nodesSignature(nodes) {
  if (!nodes || nodes.length === 0) return "";
  return nodes
    .map(
      (n) =>
        `${n.name}:${n.flow_position}:${n.capture_pct}:${n.negotiation_weight_pct}:${n.is_self ? "S" : "."}`
    )
    .join("|");
}

export default function RiverNarrativeBanner({ nodes, testIdPrefix = "river-narrative" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const currentSigRef = useRef("");
  const debounceRef = useRef(null);

  const fetchNarrative = async (force = false) => {
    if (!nodes || nodes.length < 2) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.post(`/workspace/origination/value-chain/narrative?force=${force ? "true" : "false"}`);
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Narration impossible.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh quand la signature de la rivière change (débouncé 800ms)
  useEffect(() => {
    const sig = nodesSignature(nodes);
    if (!sig || sig === currentSigRef.current) return;
    currentSigRef.current = sig;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchNarrative(false);
    }, 800);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes]);

  if (!nodes || nodes.length < 2) return null;

  const meta = data?.verdict ? VERDICT_META[data.verdict] : null;
  const Icon = meta?.icon || Waves;
  const color = meta?.color || "#EF9F27";

  return (
    <div
      className="noam-card p-3 flex items-start gap-3 border-l-2"
      style={{ borderLeftColor: color }}
      data-testid={testIdPrefix}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}20`, border: `1px solid ${color}66` }}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" style={{ color }} />
        ) : (
          <Icon className="w-4 h-4" style={{ color }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="text-[10px] uppercase tracking-wider font-semibold"
            style={{ color }}
            data-testid={`${testIdPrefix}-verdict`}
          >
            {loading ? "Analyse en cours…" : meta ? meta.label : "Narration doctrinale"}
          </span>
          {data?.strength_score != null && !loading && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded border tabular-nums"
              style={{ color, borderColor: `${color}66`, background: `${color}12` }}
              title="Position de force du self (0-100)"
              data-testid={`${testIdPrefix}-strength`}
            >
              Force {data.strength_score}/100
            </span>
          )}
          <button
            type="button"
            onClick={() => fetchNarrative(true)}
            disabled={loading}
            className="ml-auto text-[10px] text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1 disabled:opacity-40"
            title="Régénérer la narration (bypass cache)"
            data-testid={`${testIdPrefix}-refresh`}
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </button>
        </div>
        {error && (
          <p className="text-[11px] text-[#E24B4A] mt-1" data-testid={`${testIdPrefix}-error`}>{error}</p>
        )}
        {!error && data?.narrative && (
          <p
            className="text-[12px] text-zinc-200 mt-1 leading-snug"
            data-testid={`${testIdPrefix}-text`}
          >
            {data.narrative}
          </p>
        )}
        {!error && data?.key_leverage && (
          <div className="mt-1.5 inline-flex items-start gap-1.5 text-[11px]" data-testid={`${testIdPrefix}-leverage`}>
            <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color }} />
            <span className="text-zinc-300"><span className="text-zinc-500">Levier :</span> {data.key_leverage}</span>
          </div>
        )}
        {!error && !data?.narrative && !loading && (
          <p className="text-[11px] text-zinc-500 mt-1 italic">
            Aucune narration disponible. Cliquez sur « Actualiser » pour générer.
          </p>
        )}
      </div>
    </div>
  );
}
